import React, { useState, useMemo } from 'react';
import { AlertTriangle, X, Sparkles } from 'lucide-react';

const LowCreditsAlert = ({ userCredits, freeGenerationsUsed, freeTierLimit, onUpgrade }) => {
  const [isDismissed, setIsDismissed] = useState(() => {
    // Initialize from sessionStorage
    return sessionStorage.getItem('flowgen_low_credits_dismissed') === 'true';
  });

  // Memoize the alert logic
  const alertInfo = useMemo(() => {
    if (isDismissed) return null;

    let message = '';
    let urgencyLevel = 'warning'; // 'warning' or 'critical'
    
    if (userCredits) {
      const lowItems = [];
      
      if (userCredits.calendars_remaining !== -1 && userCredits.calendars_remaining <= 2 && userCredits.calendars_remaining > 0) {
        lowItems.push(`${userCredits.calendars_remaining} calendar${userCredits.calendars_remaining === 1 ? '' : 's'}`);
        if (userCredits.calendars_remaining === 1) urgencyLevel = 'critical';
      }
      if (userCredits.content_regens_remaining <= 3 && userCredits.content_regens_remaining > 0) {
        lowItems.push(`${userCredits.content_regens_remaining} content regen${userCredits.content_regens_remaining === 1 ? '' : 's'}`);
      }
      if (userCredits.image_regens_remaining <= 3 && userCredits.image_regens_remaining > 0) {
        lowItems.push(`${userCredits.image_regens_remaining} image regen${userCredits.image_regens_remaining === 1 ? '' : 's'}`);
      }
      
      if (lowItems.length > 0) {
        message = `You have ${lowItems.join(', ')} remaining.`;
      }
    } else {
      // Free tier user
      const freeRemaining = freeTierLimit - freeGenerationsUsed;
      if (freeRemaining === 1) {
        message = "You have 1 free calendar generation left.";
        urgencyLevel = 'critical';
      }
    }

    if (!message) return null;
    
    return { message, urgencyLevel };
  }, [userCredits, freeGenerationsUsed, freeTierLimit, isDismissed]);

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('flowgen_low_credits_dismissed', 'true');
  };

  if (!alertInfo) return null;

  const { message, urgencyLevel } = alertInfo;

  return (
    <div 
      className={`mx-4 mb-4 p-4 rounded-xl border backdrop-blur-sm animate-fade-in ${
        urgencyLevel === 'critical' 
          ? 'bg-red-900/20 border-red-500/50' 
          : 'bg-amber-900/20 border-amber-500/50'
      }`}
      data-testid="low-credits-alert"
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${
          urgencyLevel === 'critical' ? 'bg-red-500/20' : 'bg-amber-500/20'
        }`}>
          <AlertTriangle className={`w-5 h-5 ${
            urgencyLevel === 'critical' ? 'text-red-400' : 'text-amber-400'
          }`} />
        </div>
        
        <div className="flex-grow">
          <h4 className={`font-bold text-sm ${
            urgencyLevel === 'critical' ? 'text-red-300' : 'text-amber-300'
          }`}>
            Credits Running Low
          </h4>
          <p className="text-slate-400 text-sm mt-1">
            {message} Upgrade now to keep creating amazing content without interruption.
          </p>
          
          <div className="flex items-center gap-3 mt-3">
            <button
              onClick={onUpgrade}
              data-testid="low-credits-upgrade-btn"
              className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-all ${
                urgencyLevel === 'critical'
                  ? 'bg-red-500 hover:bg-red-400 text-white'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              View Plans
            </button>
            <button
              onClick={handleDismiss}
              data-testid="low-credits-dismiss-btn"
              className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
            >
              Remind me later
            </button>
          </div>
        </div>
        
        <button
          onClick={handleDismiss}
          className="p-1 text-slate-500 hover:text-slate-300 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default LowCreditsAlert;
